/* eslint-disable no-console */
/**
 * Frontend Logger with Distributed Tracing Support
 *
 * A structured logger that supports distributed tracing across the entire stack.
 * trace_id allows correlating logs across Frontend → Python → TypeScript → PHP services.
 *
 * ARCHITECTURE PRINCIPLE:
 * trace_id generation should ONLY happen in logger files to keep the architecture clean.
 * All other files should only import helper functions from this module.
 *
 * WHAT IS A "FLOW"?
 * A flow is a single logical user action or system event from start to finish.
 * Examples:
 *   - User clicks "Send Message" → message delivered (one flow)
 *   - User opens conversation → data loaded (one flow)
 *   - User creates a sequence → sequence saved (one flow)
 *
 * WHEN TO START A NEW TRACE:
 * Call `startTrace()` at the BEGINNING of each user-initiated action.
 * This ensures each action gets its own trace_id for debugging.
 *
 * Usage:
 *   import { logger, startTrace, getTraceId } from '@/lib/logger';
 *
 *   // CORRECT: Start new trace for each user action
 *   const handleSendMessage = async () => {
 *     startTrace(); // New trace_id for this action
 *     logger.info('User clicked send message');
 *     await apiClient.sendMessage(...);
 *   };
 *
 *   // The trace_id will be automatically included in:
 *   // - All console logs (via interceptors)
 *   // - All HTTP requests (via X-Trace-Id header in axios)
 *
 * TRACE ID FORMAT:
 *   {timestamp_base36}-{random8chars}
 *   Example: lxc2abc3-f7e12345
 *
 * This matches the backend format for consistency across services.
 */

// ============================================================================
// TYPES
// ============================================================================

export type LogLevel = 'DEBUG' | 'INFO' | 'WARN' | 'ERROR';

export interface LogExtra {
  [key: string]: unknown;
}

// ============================================================================
// TRACE ID MANAGEMENT
// ============================================================================

/**
 * Current trace ID for the active flow.
 * Will be auto-generated if accessed when null.
 */
let currentTraceId: string | null = null;

/**
 * Generate a unique trace ID for distributed tracing.
 * Format: {timestamp_base36}-{random8chars} for sortability and uniqueness.
 *
 * This matches the backend format (TypeScript & Python) for consistency.
 *
 * @example
 * const traceId = generateTraceId();
 * // Returns: "lxc2abc3-f7e12345"
 */
export function generateTraceId(): string {
  const timestamp = Date.now().toString(36); // Base36 for compactness
  const random = Math.random().toString(36).substring(2, 10); // 8 random chars
  return `${timestamp}-${random}`;
}

/**
 * Start a new trace flow. Generates a fresh trace_id.
 * Call this at the beginning of a user-initiated action.
 *
 * @returns The newly generated trace_id
 *
 * @example
 * // User clicks "Send Message"
 * startTrace();
 * logger.info('Sending message to lead');
 * await apiClient.sendMessage(...);
 */
export function startTrace(): string {
  currentTraceId = generateTraceId();
  return currentTraceId;
}

/**
 * Get the current trace ID.
 * Auto-generates a new trace_id if none exists.
 *
 * Use this when you need to include trace_id in headers or payloads.
 *
 * @returns The current trace_id (auto-generates if none)
 *
 * @example
 * // In axios interceptor:
 * config.headers['X-Trace-Id'] = getTraceId();
 */
export function getTraceId(): string {
  if (!currentTraceId) {
    currentTraceId = generateTraceId();
  }
  return currentTraceId;
}

/**
 * Clear the current trace. Call when a flow ends.
 * The next call to getTraceId() will generate a new traceId.
 *
 * @example
 * // After completing a user action
 * clearTrace();
 */
export function clearTrace(): void {
  currentTraceId = null;
}

// ============================================================================
// CONSOLE INTERCEPTORS
// ============================================================================

/**
 * Runtime detection for proper logging strategy:
 * - isBrowser: True if running in browser (has window)
 * - isServer: True if not browser (Node or Edge runtime)
 */
const isBrowser = typeof window !== 'undefined';

// Server = Node or Edge (anything not browser)
const isServer = !isBrowser;

/**
 * Store original console methods for raw access and interception.
 * IMPORTANT: These must be captured BEFORE installConsoleInterceptors() is called.
 */
const originalConsole = {
  log: console.log.bind(console),
  info: console.info.bind(console),
  warn: console.warn.bind(console),
  error: console.error.bind(console),
  debug: console.debug.bind(console),
};

/**
 * Write directly to stdout/stderr for server-side logging.
 * Uses the original console methods to ensure proper capture by CloudWatch.
 *
 * Note: We use console.log/error instead of process.stdout.write because:
 * 1. AWS CloudWatch Logs driver captures console output more reliably
 * 2. process.stdout.write can have buffering issues
 * 3. Console methods are synchronous and flush immediately
 */
const writeToStdout = (data: string) => originalConsole.log(data);
const writeToStderr = (data: string) => originalConsole.error(data);

/**
 * Safely serialize a value to string for logging.
 * Handles circular references, Error objects, and other edge cases.
 */
function safeSerialize(value: unknown): string {
  if (value === null) return 'null';
  if (value === undefined) return 'undefined';
  if (typeof value === 'string') return value;

  // Handle Error objects specially - preserve name, message, stack
  if (value instanceof Error) {
    return JSON.stringify({
      error_name: value.name,
      error_message: value.message,
      error_stack: value.stack,
    });
  }

  try {
    return JSON.stringify(value);
  } catch {
    // Handle circular references or other stringify failures
    try {
      return String(value);
    } catch {
      return '[Unserializable value]';
    }
  }
}

/**
 * Safely stringify an object, handling circular references and errors.
 */
function safeStringify(obj: Record<string, unknown>): string {
  try {
    return JSON.stringify(obj);
  } catch {
    // Fallback: stringify each field individually
    const safeObj: Record<string, unknown> = {};
    for (const [key, value] of Object.entries(obj)) {
      try {
        // Test if value is serializable
        JSON.stringify(value);
        safeObj[key] = value;
      } catch {
        safeObj[key] = safeSerialize(value);
      }
    }
    try {
      return JSON.stringify(safeObj);
    } catch {
      return JSON.stringify({ message: '[Serialization failed]', level: obj.level || 'ERROR' });
    }
  }
}

/**
 * Extract useful info from an Error object for logging.
 */
function extractErrorInfo(error: Error): Record<string, unknown> {
  return {
    error_name: error.name,
    error_message: error.message,
    error_stack: error.stack,
  };
}

/**
 * Format log output as structured JSON for CloudWatch.
 * All fields are top-level for easy querying.
 *
 * @param level - Log level (DEBUG, INFO, WARN, ERROR)
 * @param args - Arguments passed to console method
 * @returns For client: formatted args array. For server: outputs directly to stdout.
 */
function formatLogOutput(level: LogLevel, args: unknown[]): unknown[] | null {
  const traceId = getTraceId(); // Auto-generates if none exists
  const timestamp = new Date().toISOString();

  if (isServer) {
    // Server-side: Output clean JSON directly to stdout (bypass console)

    // Check if the first arg is already a structured JSON log string
    // (e.g. from edgeLog in middleware which does console.log(JSON.stringify({...})))
    // to avoid double-encoding the log output.
    if (typeof args[0] === 'string' && args.length === 1) {
      try {
        const parsed = JSON.parse(args[0]);
        if (parsed && typeof parsed === 'object' && 'message' in parsed && 'level' in parsed) {
          // Already a structured log — write it directly without re-wrapping
          if (level === 'ERROR' || level === 'WARN') {
            writeToStderr(args[0]);
          } else {
            writeToStdout(args[0]);
          }
          return null;
        }
      } catch {
        // Not valid JSON, continue with normal formatting
      }
    }

    // First arg is the message (string), rest are extra data
    let message: string;
    if (typeof args[0] === 'string') {
      message = args[0];
    } else if (args[0] instanceof Error) {
      message = args[0].message;
    } else {
      message = safeSerialize(args[0]);
    }

    const extra =
      args.length > 1
        ? args.slice(1).reduce<Record<string, unknown>>((acc, arg, idx) => {
            if (arg instanceof Error) {
              return { ...acc, ...extractErrorInfo(arg) };
            }
            if (typeof arg === 'object' && arg !== null) {
              // Safely merge object, handling circular refs
              try {
                JSON.stringify(arg); // Test serializability
                return { ...acc, ...(arg as Record<string, unknown>) };
              } catch {
                return { ...acc, [`arg${idx + 1}`]: safeSerialize(arg) };
              }
            }
            return { ...acc, [`arg${idx + 1}`]: arg };
          }, {})
        : {};

    // If first arg was an Error, include its details
    if (args[0] instanceof Error) {
      Object.assign(extra, extractErrorInfo(args[0]));
    }

    const output = safeStringify({
      message,
      level,
      timestamp,
      trace_id: traceId,
      service: 'Frontend',
      ...extra,
    });

    // Write directly to stdout/stderr
    if (level === 'ERROR' || level === 'WARN') {
      writeToStderr(output);
    } else {
      writeToStdout(output);
    }

    return null; // Signal that we've already output
  }

  // Client-side: Keep human-readable format for browser console
  return [`[${timestamp}] [${level}] [trace_id:${traceId}]`, ...args];
}

/**
 * Install console interceptors to automatically include traceId in all console calls.
 * Call this once at app startup (in _app.tsx or main.tsx).
 *
 * After installation:
 * - Server-side: Outputs structured JSON for CloudWatch
 * - Client-side: Outputs human-readable format for browser console
 *
 * @example
 * // In _app.tsx
 * import { installConsoleInterceptors } from '@/lib/logger';
 * installConsoleInterceptors();
 *
 * // Server output (JSON): {"message":"Hello","level":"INFO","timestamp":"...","trace_id":"abc123","service":"Frontend"}
 * // Browser output: [2024-01-15T10:30:00.000Z] [INFO] [trace_id:abc123] Hello
 */
export function installConsoleInterceptors(): void {
  // Install interceptors in both browser and server environments
  // Server-side logs go to stdout/stderr and are captured by CloudWatch
  console.log = (...args: unknown[]) => {
    const output = formatLogOutput('INFO', args);
    if (output) originalConsole.log(...output);
  };
  console.info = (...args: unknown[]) => {
    const output = formatLogOutput('INFO', args);
    if (output) originalConsole.info(...output);
  };
  console.warn = (...args: unknown[]) => {
    const output = formatLogOutput('WARN', args);
    if (output) originalConsole.warn(...output);
  };
  console.error = (...args: unknown[]) => {
    const output = formatLogOutput('ERROR', args);
    if (output) originalConsole.error(...output);
  };
  console.debug = (...args: unknown[]) => {
    const output = formatLogOutput('DEBUG', args);
    if (output) originalConsole.debug(...output);
  };
}

// ============================================================================
// SERVER-SIDE LOGGING (for CloudWatch)
// ============================================================================

/**
 * Server-side logger that outputs structured JSON logs for CloudWatch.
 * Use this for server-side logging (API routes, getServerSideProps, middleware).
 *
 * All fields are top-level for easy CloudWatch querying:
 * - message: The log message (string only)
 * - level: DEBUG, INFO, WARN, ERROR
 * - timestamp: ISO timestamp
 * - trace_id: Distributed tracing ID
 * - service: "Frontend"
 * - ...extra: Any additional fields
 *
 * @example
 * import { serverLogger } from '@/lib/logger';
 *
 * // In API route or getServerSideProps:
 * serverLogger.info('Processing request', { userId: '123', action: 'login' });
 * // Output: {"message":"Processing request","level":"INFO","timestamp":"...","trace_id":"...","service":"Frontend","userId":"123","action":"login"}
 */
export const serverLogger = {
  /**
   * Log at DEBUG level (server-side, JSON format)
   */
  debug: (message: string, extra?: LogExtra) => {
    if (isServer) {
      writeToStdout(
        JSON.stringify({
          message,
          level: 'DEBUG',
          timestamp: new Date().toISOString(),
          trace_id: getTraceId(),
          service: 'Frontend',
          ...extra,
        })
      );
    }
  },

  /**
   * Log at INFO level (server-side, JSON format)
   */
  info: (message: string, extra?: LogExtra) => {
    if (isServer) {
      writeToStdout(
        JSON.stringify({
          message,
          level: 'INFO',
          timestamp: new Date().toISOString(),
          trace_id: getTraceId(),
          service: 'Frontend',
          ...extra,
        })
      );
    }
  },

  /**
   * Log at WARN level (server-side, JSON format)
   */
  warn: (message: string, extra?: LogExtra) => {
    if (isServer) {
      writeToStderr(
        JSON.stringify({
          message,
          level: 'WARN',
          timestamp: new Date().toISOString(),
          trace_id: getTraceId(),
          service: 'Frontend',
          ...extra,
        })
      );
    }
  },

  /**
   * Log at ERROR level (server-side, JSON format)
   */
  error: (message: string, extra?: LogExtra) => {
    if (isServer) {
      writeToStderr(
        JSON.stringify({
          message,
          level: 'ERROR',
          timestamp: new Date().toISOString(),
          trace_id: getTraceId(),
          service: 'Frontend',
          ...extra,
        })
      );
    }
  },
};

// ============================================================================
// LOGGER INSTANCE
// ============================================================================

/**
 * Logger with traceId-aware methods.
 * - Server-side: Outputs structured JSON for CloudWatch
 * - Client-side: Outputs human-readable format for browser console
 *
 * @example
 * import { logger } from '@/lib/logger';
 *
 * logger.info('Processing user request');
 * logger.error('Failed to send message', { error: err.message });
 */
export const logger = {
  /**
   * Log at DEBUG level with traceId
   */
  debug: (...args: unknown[]) => {
    const output = formatLogOutput('DEBUG', args);
    if (output) originalConsole.debug(...output);
  },

  /**
   * Log at INFO level with traceId
   */
  log: (...args: unknown[]) => {
    const output = formatLogOutput('INFO', args);
    if (output) originalConsole.log(...output);
  },

  /**
   * Log at INFO level with traceId (alias for log)
   */
  info: (...args: unknown[]) => {
    const output = formatLogOutput('INFO', args);
    if (output) originalConsole.info(...output);
  },

  /**
   * Log at WARN level with traceId
   */
  warn: (...args: unknown[]) => {
    const output = formatLogOutput('WARN', args);
    if (output) originalConsole.warn(...output);
  },

  /**
   * Log at ERROR level with traceId
   */
  error: (...args: unknown[]) => {
    const output = formatLogOutput('ERROR', args);
    if (output) originalConsole.error(...output);
  },

  /**
   * Access to original console methods without traceId prefix.
   * Use this when you need raw console output.
   */
  raw: originalConsole,
};

export default logger;
